import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchEmpDiscripency } from "../../action/EmpDiscripencyAction";


const BRAND = "#330066";

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => new Date(d).toISOString().split("T")[0];
const formatTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export default function AllocationDiscrepancy() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((s) => s.empDiscripency);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    dispatch(fetchEmpDiscripency(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const totalEmployees = data?.length || 0;

  // ── PDF export ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text("Employee Allocation Discrepancy Report", pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.15, "Date – ", formatDate(selectedDate)],
      [sx + tw * 0.5,  "Time – ", formatTime(at)],
      [sx + tw * 0.85, "Total – ", totalEmployees.toString()],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    autoTable(doc, {
      startY: 52,
      head: [["Location", "Service No", "Name", "Designation", "Mobile"]],
      body: data?.map((item) => [
        item.Emp_location || "",
        item.Emp_serviceNo || "",
        item.Emp_name || "",
        item.Emp_designation || "",
        item.Emp_mobile || "",
      ]),
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 25, halign: "center" },
        1: { cellWidth: 28, halign: "center" },
        2: { cellWidth: 50 },
        3: { cellWidth: 48 },
        4: { cellWidth: 36, halign: "center" },
      },
      theme: "grid",
      margin: { left: 10, right: 10 },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Employee_Discrepancy_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {/* Date pill */}
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setOpenDatePicker(false);
              }}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats + PDF row ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "stretch" }}>
            {/* Total card */}
            <Box
              sx={{
                flex: 1, background: "#fff", border: "0.5px solid #e2e8f0",
                borderRadius: "12px", p: "10px 12px",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
                Total Employees
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#dc2626", lineHeight: 1.1 }}>
                {totalEmployees}
              </Typography>
            </Box>

            {/* PDF button */}
            <Button
              size="small"
              onClick={handleExportPDF}
              disabled={!data?.length}
              sx={{
                minWidth: 0, px: "14px", borderRadius: "12px",
                background: "#16a34a", color: "#fff",
                textTransform: "none", flexShrink: 0,
                display: "flex", flexDirection: "column", gap: "2px",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ fontSize: 10, fontWeight: 500 }}>PDF</Box>
            </Button>
          </Box>
        )}

        {/* ── Table ── */}
        <SectionLabel>Discrepancy records</SectionLabel>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            <TableContainer sx={{ maxHeight: "calc(100vh - 260px)", overflowX: "auto" }}>
              <Table size="small" stickyHeader sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Location",    w: "12%", align: "center" },
                      { label: "Service No",      w: "13%", align: "center" },
                      { label: "Name",        w: "30%", align: "left"   },
                      { label: "Designation", w: "30%", align: "left"   },
                      { label: "Mobile",      w: "15%", align: "center" },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        sx={{
                          width: h.w, background: BRAND, color: "#fff",
                          fontSize: 11, fontWeight: 600, py: "8px", px: "8px",
                          borderBottom: "none", textAlign: h.align,
                          position: "sticky", top: 0, zIndex: 10,
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data?.length > 0 ? (
                    data.map((item, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(21,76,121,0.04)" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {/* Location */}
                        <TableCell sx={{ textAlign: "center", px: "8px", py: "7px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block", px: "7px", py: "1px",
                              borderRadius: "8px", fontSize: 11, fontWeight: 600,
                              background: "#e8f0fe", color: BRAND,
                            }}
                          >
                            {item.Emp_location}
                          </Box>
                        </TableCell>

                        {/* Service No */}
                        <TableCell
                          sx={{
                            textAlign: "center", fontSize: 12, fontWeight: 600,
                            color: "#475569", px: "8px", py: "7px",
                          }}
                        >
                          {item.Emp_serviceNo}
                        </TableCell>

                        {/* Name */}
                        <TableCell
                          sx={{
                            fontSize: 12, fontWeight: 500, px: "8px", py: "7px",
                            whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3,
                          }}
                        >
                          {item.Emp_name}
                        </TableCell>

                        {/* Designation */}
                        <TableCell
                          sx={{
                            fontSize: 11, color: "#64748b", px: "8px", py: "7px",
                            whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3,
                          }}
                        >
                          {item.Emp_designation}
                        </TableCell>

                        {/* Mobile */}
                        <TableCell sx={{ textAlign: "center", px: "8px", py: "7px" }}>
                          {item.Emp_mobile ? (
                            <Box
                              component="a"
                              href={`tel:${item.Emp_mobile}`}
                              sx={{
                                display: "inline-block", px: "7px", py: "1px",
                                borderRadius: "8px", fontSize: 11, fontWeight: 500,
                                background: "#dcfce7", color: "#15803d",
                                textDecoration: "none",
                                "&:hover": { background: "#bbf7d0" },
                              }}
                            >
                              {item.Emp_mobile}
                            </Box>
                          ) : (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block", px: "7px", py: "1px",
                                borderRadius: "8px", fontSize: 11,
                                background: "#fee2e2", color: "#b91c1c",
                              }}
                            >
                              N/A
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}